/**
 * ProfitSpot Sidebar Navigation
 * Desktop: collapsible fixed sidebar
 * Mobile: hidden by default, slides in as drawer with backdrop
 */
import React, { useState, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import {
  TrendingUp, Users, FlaskConical, Activity, Brain,
  ChevronLeft, ChevronRight, Zap, LogOut, User,
  Crown, X, Menu, BarChart3, Settings, Bell, Wallet, LayoutGrid, Gauge
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import AccountModal from '../AccountModal';
import ComparePlans from '../ComparePlans';

const Sidebar = ({ mobileOpen, onMobileClose, onCollapsedChange }) => {
  const [collapsed, setCollapsed] = useState(() => {
    const saved = localStorage.getItem('profitspot_sidebar_collapsed');
    return saved === 'true';
  });

  const { user, isAuthenticated, logout } = useAuth();
  const [showAuthModal, setShowAuthModal] = useState(false);

  // Listen for global auth open event (from UpgradeTeaser, etc.)
  React.useEffect(() => {
    const handler = () => setShowAuthModal(true);
    window.addEventListener('profitspot:open-auth', handler);
    return () => window.removeEventListener('profitspot:open-auth', handler);
  }, []);
  const [showAccountModal, setShowAccountModal] = useState(false);
  const [showComparePlans, setShowComparePlans] = useState(false);
  const location = useLocation();

  const userTier = user?.subscription?.tier || 'free';
  const isAdmin = user?.role === 'admin';
  const isPremium = userTier === 'pro' || userTier === 'elite' || userTier === 'unlimited';

  // Close mobile drawer on route change
  useEffect(() => {
    onMobileClose?.();
  }, [location.pathname]);

  const toggleCollapse = () => {
    const newState = !collapsed;
    setCollapsed(newState);
    localStorage.setItem('profitspot_sidebar_collapsed', newState.toString());
    onCollapsedChange?.(newState);
  };

  const isElite = userTier === 'elite' || userTier === 'unlimited' || isAdmin;

  const navItems = [
    { path: '/explore', icon: TrendingUp, label: 'Yield Explorer', description: 'Yields + AI Intelligence', secondaryIcon: Brain },
    { path: '/simulator', icon: FlaskConical, label: 'Simulator Labs', description: 'What-if engine' },
    { 
      path: '/forecast', 
      icon: Gauge, 
      label: 'Forecast Dial', 
      description: 'Monte Carlo predictions',
      gated: !isElite,
    },
    { path: '/portfolio', icon: Wallet, label: 'Portfolio Analyzer', description: 'LP positions' },
  ];

  if (isAuthenticated) {
    navItems.push({
      path: '/whales', icon: Users, label: 'Whale Tracker',
      description: 'Smart money', gated: !isPremium,
    });
  } else {
    navItems.push({
      path: '/whales', icon: Users, label: 'Whale Tracker',
      description: 'Smart money', locked: true,
    });
  }

  if (isAdmin) {
    navItems.push({
      path: '/node-health', icon: Activity, label: 'Node Health',
      description: 'Mission Control', adminOnly: true, pulse: true,
    });
    navItems.push({
      path: '/admin-dashboard', icon: BarChart3, label: 'Analytics',
      description: 'Platform stats', adminOnly: true,
    });
  }

  // Alert Preferences for Pro/Elite users
  if (isPremium) {
    navItems.push({
      path: '/alert-preferences', icon: Bell, label: 'Alert Settings',
      description: 'Customize alerts',
    });
  }

  const NavItems = ({ isMobile = false }) => (
    <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
      {navItems.map((item) => {
        const isActive = location.pathname === item.path.split('?')[0];
        const Icon = item.icon;
        const showLabel = isMobile || !collapsed;

        return (
          <NavLink
            key={item.path}
            to={item.path}
            onClick={(e) => {
              if (item.locked) {
                e.preventDefault();
                toast('Sign in to access this feature.');
                setShowAuthModal(true);
                return;
              }
              if (item.gated) {
                e.preventDefault();
                toast('Upgrade to Elite to access this feature.');
                setShowComparePlans(true);
                return;
              }
            }}
            className={`group flex items-center gap-3 px-3 py-3 rounded-lg transition-all ${
              isActive
                ? 'bg-[#00ff88]/15 text-[#00ff88] border border-[#00ff88]/30'
                : 'text-gray-400 hover:text-white hover:bg-gray-800/50'
            } ${item.adminOnly ? 'border-l-2 border-amber-500/50 bg-amber-500/5 hover:bg-amber-500/10' : ''
            } ${item.pulse ? 'animate-pulse-subtle' : ''
            } ${item.locked ? 'opacity-70 cursor-not-allowed' : ''} ${item.highlight ? 'border border-[#00d4ff]/20 bg-[#00d4ff]/5' : ''}`}
            data-testid={`nav-${item.path.replace('/', '') || 'home'}`}
          >
            <div className="relative flex-shrink-0">
              <Icon className="w-5 h-5" />
              {item.gated && <div className="absolute -top-1 -right-1 w-2 h-2 bg-[#ffa502] rounded-full" />}
              {item.pulse && <div className="absolute -inset-1 bg-amber-500/20 rounded-full animate-ping" />}
            </div>

            {showLabel && (
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium truncate">{item.label}</span>
                  {item.secondaryIcon && <item.secondaryIcon className="w-3.5 h-3.5 text-[#00d4ff] flex-shrink-0" />}
                  {item.gated && <Crown className="w-3 h-3 text-[#ffa502] flex-shrink-0" />}
                  {item.locked && <span className="text-xs">🔒</span>}
                  {item.adminOnly && (
                    <span className="text-[9px] px-1.5 py-0.5 bg-amber-500/30 text-amber-300 rounded font-bold uppercase">
                      Admin
                    </span>
                  )}
                </div>
                <p className="text-[10px] text-gray-500 truncate">{item.description}</p>
              </div>
            )}
          </NavLink>
        );
      })}
    </nav>
  );

  const UserSection = ({ isMobile = false }) => {
    const showLabel = isMobile || !collapsed;
    return (
      <div className="p-3 border-t border-gray-800">
        {isAuthenticated ? (
          <div className="space-y-2">
            {showLabel && (
              <div className="px-2">
                <p className="text-xs text-gray-400 truncate">{user?.email}</p>
                <div className="flex items-center gap-2 mt-1">
                  <span className={`text-[10px] px-2 py-0.5 rounded font-bold uppercase ${
                    userTier !== 'free' ? 'bg-[#00ff88]/10 text-[#00ff88]' : 'bg-gray-700 text-gray-300'
                  }`}>
                    {userTier}
                  </span>
                  {isAdmin && (
                    <span className="text-[10px] px-2 py-0.5 rounded font-bold uppercase bg-amber-500/20 text-amber-400">Admin</span>
                  )}
                </div>
              </div>
            )}
            {/* Account Settings button */}
            <button
              onClick={() => setShowAccountModal(true)}
              className="w-full flex items-center justify-center gap-2 px-3 py-2 text-gray-400 hover:text-white hover:bg-gray-800/50 rounded-lg transition-colors"
              data-testid="sidebar-account-settings"
            >
              <Settings className="w-4 h-4" />
              {showLabel && <span className="text-xs">Account Settings</span>}
            </button>
            {/* Pricing / Compare plans */}
            <button
              onClick={() => setShowComparePlans(true)}
              className="w-full flex items-center justify-center gap-2 px-3 py-2 text-gray-400 hover:text-white hover:bg-gray-800/50 rounded-lg transition-colors"
              data-testid="sidebar-pricing"
            >
              <LayoutGrid className="w-4 h-4 flex-shrink-0" />
              {showLabel && <span className="text-xs">Compare Plans</span>}
            </button>
            <button
              onClick={logout}
              className="w-full flex items-center justify-center gap-2 px-3 py-2 text-gray-400 hover:text-white hover:bg-gray-800/50 rounded-lg transition-colors"
              data-testid="sidebar-logout"
            >
              <LogOut className="w-4 h-4" />
              {showLabel && <span className="text-xs">Sign Out</span>}
            </button>
          </div>
        ) : (
          <div className="space-y-2">
            {/* Pricing — prominent for unauthenticated users */}
            <button
              onClick={() => setShowComparePlans(true)}
              className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg border border-[#00ff88]/25 bg-[#00ff88]/08 text-[#00ff88] hover:bg-[#00ff88]/15 transition-colors"
              data-testid="sidebar-pricing"
            >
              <LayoutGrid className="w-4 h-4 flex-shrink-0" />
              {showLabel && <span className="text-xs font-medium">Compare Plans</span>}
            </button>
            <button
              onClick={() => setShowAuthModal(true)}
              className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-[#00ff88]/10 text-[#00ff88] rounded-lg hover:bg-[#00ff88]/20 transition-colors"
              data-testid="sidebar-signin"
            >
              <User className="w-4 h-4" />
              {showLabel && <span className="text-sm font-medium">Sign In</span>}
            </button>
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      {/* ── DESKTOP SIDEBAR ── */}
      <aside
        className={`hidden lg:flex fixed left-0 top-0 h-full bg-[#0a0a12] border-r border-gray-800 z-40 flex-col transition-all duration-300 ${
          collapsed ? 'w-16' : 'w-56'
        }`}
        data-testid="sidebar"
      >
        {/* Logo */}
        <div className="p-4 border-b border-gray-800">
          <div className="flex items-center gap-2">
            <Zap className="h-6 w-6 text-[#00ff88] flex-shrink-0" />
            {!collapsed && (
              <h1 className="font-bold text-base tracking-tight text-white">
                PROFIT<span className="text-[#00ff88]">SPOT</span>
              </h1>
            )}
          </div>
        </div>

        <NavItems />
        <UserSection />

        {/* Collapse toggle */}
        <button
          onClick={toggleCollapse}
          className="absolute -right-3 top-20 w-6 h-6 bg-[#1a1a2e] border border-gray-700 rounded-full flex items-center justify-center text-gray-400 hover:text-white hover:border-[#00ff88]/50 transition-colors"
          data-testid="sidebar-toggle"
        >
          {collapsed ? <ChevronRight className="w-3 h-3" /> : <ChevronLeft className="w-3 h-3" />}
        </button>
      </aside>

      {/* ── MOBILE DRAWER BACKDROP ── */}
      {mobileOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/70 backdrop-blur-sm z-40"
          onClick={onMobileClose}
          data-testid="mobile-backdrop"
        />
      )}

      {/* ── MOBILE DRAWER ── */}
      <aside
        className={`lg:hidden fixed left-0 top-0 h-full w-72 bg-[#0a0a12] border-r border-gray-800 z-50 flex flex-col transition-transform duration-300 ease-in-out ${
          mobileOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        data-testid="mobile-sidebar"
      >
        {/* Mobile header */}
        <div className="p-4 border-b border-gray-800 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Zap className="h-6 w-6 text-[#00ff88]" />
            <h1 className="font-bold text-base tracking-tight text-white">
              PROFIT<span className="text-[#00ff88]">SPOT</span>
            </h1>
          </div>
          <button
            onClick={onMobileClose}
            className="p-1.5 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors"
            data-testid="mobile-close-btn"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <NavItems isMobile />
        <UserSection isMobile />
      </aside>

      {showAuthModal && <AuthModal onClose={() => setShowAuthModal(false)} />}
      {showAccountModal && <AccountModal onClose={() => setShowAccountModal(false)} />}
      <ComparePlans open={showComparePlans} onClose={() => setShowComparePlans(false)} />
    </>
  );
};

// ─── Auth Modal ──────────────────────────────────────────────
const AuthModal = ({ onClose }) => {
  const { login, register } = useAuth();
  const [mode, setMode] = useState('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [refCode, setRefCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Pre-fill referral code from URL param ?ref=CODE
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ref = params.get('ref');
    if (ref) { setRefCode(ref.toUpperCase()); setMode('register'); }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      if (mode === 'login') await login(email, password);
      else await register(email, password, name, refCode.trim() || undefined);
      onClose();
    } catch (err) {
      console.error('Auth error:', err);
      setError(mode === 'login' ? 'Incorrect email or password.' : 'Couldn’t create account. Please try again.');
    }
    setLoading(false);
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[100] flex items-center justify-center p-4" data-testid="auth-modal">
      <div className="bg-[#1a1a2e] max-w-sm w-full rounded-xl p-6 border border-gray-700">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-lg font-bold text-white">{mode === 'login' ? 'Sign In' : 'Create Account'}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          {mode === 'register' && (
            <input data-testid="auth-name" value={name} onChange={e => setName(e.target.value)} placeholder="Name" className="w-full bg-[#0f0f1a] border border-gray-700 rounded-lg px-3 py-2.5 text-sm text-white focus:outline-none focus:border-[#00ff88]" />
          )}
          <input data-testid="auth-email" value={email} onChange={e => setEmail(e.target.value)} placeholder="Email or Username" type="text" required className="w-full bg-[#0f0f1a] border border-gray-700 rounded-lg px-3 py-2.5 text-sm text-white focus:outline-none focus:border-[#00ff88]" />
          <input data-testid="auth-password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Password" type="password" required className="w-full bg-[#0f0f1a] border border-gray-700 rounded-lg px-3 py-2.5 text-sm text-white focus:outline-none focus:border-[#00ff88]" />
          {mode === 'register' && (
            <input
              data-testid="auth-referral-code"
              value={refCode}
              onChange={e => setRefCode(e.target.value.toUpperCase())}
              placeholder="Referral code (optional)"
              className="w-full bg-[#0f0f1a] border border-gray-700 rounded-lg px-3 py-2.5 text-sm text-white focus:outline-none focus:border-[#00ff88] font-mono tracking-wider placeholder:font-sans placeholder:tracking-normal"
            />
          )}
          {error && (
            <p className="text-xs text-red-400" role="alert" data-testid="auth-error">
              {error}
            </p>
          )}
          <button data-testid="auth-submit" type="submit" disabled={loading} className="w-full bg-[#00ff88] text-black font-bold py-2.5 rounded-lg disabled:opacity-50">
            {loading ? 'Please wait...' : mode === 'login' ? 'Sign In' : 'Register'}
          </button>
        </form>
        <p className="text-center text-sm text-gray-400 mt-4">
          {mode === 'login' ? "Don't have an account? " : 'Already have an account? '}
          <button onClick={() => setMode(mode === 'login' ? 'register' : 'login')} className="text-[#00ff88] hover:underline" data-testid="auth-toggle">
            {mode === 'login' ? 'Register' : 'Sign In'}
          </button>
        </p>
      </div>
    </div>
  );
};

export default Sidebar;
